/**
 * Facet dropdown options for the Contacts list, extracted from the page so the
 * option lists can be unit tested without mounting the table.
 *
 * The keys here are HALF of a contract with `Api::V1::ContactsController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `CONTACTS_TABLE.facetKeys`, and
 * by `xop-brain/apps/crm-api/test/controllers/concerns/declared_keys_export_test.rb`
 * on the server side.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { CONTACTS_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import type { ContactFilters } from "@crm-src/lib/contacts-api";

/**
 * The Contacts facet keys, DERIVED from the shared config rather than re-typed
 * — so there is exactly one place a key can be misspelled, and misspelling it
 * there breaks the assertion below.
 */
export type ContactFacetKey = FacetKeysOf<typeof CONTACTS_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param `ContactFilters`
 * actually carries — i.e. one the API implements. A key that only exists in the
 * config otherwise compiles fine and fails at RUNTIME as a param the server
 * drops, with `config-parity.test.ts` as the only net.
 */
type FacetKeysAreFilterFields =
  ContactFacetKey extends keyof ContactFilters ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

/**
 * The two values the `status` facet sends, mirroring `contacts.active`.
 *
 * There is NO `contacts.status` column — verified against
 * `xop-brain/apps/crm-api/db/schema.rb`, which has a `boolean "active"` and
 * nothing else status-shaped. "Status" is the TABLE's name for that boolean
 * (the row chip reads Active/Inactive), and the controller's `status` facet
 * maps these two labels back onto it, narrowing to NOTHING for any third value
 * rather than widening. Inventing a value outside this pair would not be a loud
 * error anywhere — the dropdown renders, the query goes out, and the server
 * matches zero rows. Pinned by `contacts-page-query.test.ts`.
 */
const STATUSES: FacetDef["options"] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/**
 * Build the Contacts filter-bar facets.
 *
 * Options come from data the page ALREADY has — the shared industry catalogue
 * and the user roster — so no endpoint exists just to populate a dropdown.
 *
 * Returns every declared facet, INCLUDING one whose options came back empty
 * (`useUsers()` answers 403 with `[]` for a non-admin): dropping an optionless
 * facet from the rendered controls is `TableFilterBar`'s job, so every list
 * page inherits it. This stays a pure function of its inputs.
 */
export function buildContactFacetDefs(
  industries: readonly string[],
  users: PersonLike[],
): FacetDef<ContactFacetKey>[] {
  return [
    {
      key: "industry",
      label: "Industry",
      options: industries.map((i) => ({ value: i, label: i })),
    },
    { key: "status", label: "Status", options: STATUSES },
    {
      key: "created_by_id",
      label: "Created By",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
  ];
}
